'use client'

import { NATIVE_HYPE_GAS_RESERVE } from '@/config/consts'
import {
  ValidationAlert,
  createNativeHypeToken,
  getTokenFromMap,
  isWhypeVault,
  useAvailableSubAccount,
  useTokensInfo,
  validateEulerBorrow,
  validateMaxDeposit,
} from '@/domain/euler'
import { getSubAccountAddress } from '@/domain/euler/shared/subAccounts'
import { useOpenDialog } from '@/domain/hooks/useDialog'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { NormalizedUnitNumber, Percentage } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { BORROW_VAULT_CHAIN_ID } from '@/features/eborrow/logic/useVaultList'
import { usePreviewSupplyAPY } from '@/features/elend/logic/usePreviewSupplyAPY'
import { Button } from '@/ui/atoms/button/Button'
import { Panel } from '@/ui/atoms/panel/Panel'
import { Slider } from '@/ui/atoms/slider/Slider'
import { TabSelector } from '@/ui/atoms/tab-selector/TabSelector'
import { AssetSelector } from '@/ui/molecules/asset-selector/AssetSelector'
import { ApyChange } from '@/ui/atoms/apy-change/ApyChange'
import { SlippagePanel } from '@/ui/molecules/slippage-panel/SlippagePanel'
import { SupplyApyTooltip } from '@/ui/molecules/supply-apy-tooltip/SupplyApyTooltip'
import { ControlledMultiSelectorAssetInput } from '@/ui/organisms/multi-selector/ControlledMultiSelectorAssetInput'
import { normalizeDecimalInput } from '@/utils/decimalInput'
import { usePrivy } from '@privy-io/react-auth'
import { eVaultImplementationAbi } from 'abis/evm/eVaultImplementation'
import { eulerVaultConnectorAbi } from 'abis/evm/eulerVaultConnector'
import { useEffect, useMemo, useRef, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Address, erc20Abi, formatUnits, parseUnits } from 'viem'
import { useAccount, useBalance, useReadContract, useReadContracts } from 'wagmi'
import { evcBorrowDialogConfig } from '../dialogs/borrow/EvcBorrowDialog'
import { addIntrinsicAPY } from '@/domain/euler/shared/apyUtils'
import { evcMultiplyDialogConfig } from '../dialogs/multiply/EvcMultiplyDialog'

export interface BorrowMultiplyTabsProps {
  collateral: Address
  liability: Address
  collateralSymbol?: string
  liabilitySymbol?: string
  maxLTV?: bigint
  liquidationLTV?: bigint
  borrowAPY?: number
  /** Collateral supply APY (used for ROE calculation in Position Summary) */
  supplyAPY?: number
  /** Intrinsic APY of the collateral asset (e.g. staking yield for wstHYPE) — from indexer */
  collateralIntrinsicAPY?: number
  /** Lending APY of the collateral vault (interest from borrowers) — from indexer */
  collateralLendingAPY?: number
  evcAddress?: Address
  collateralPriceUSD?: number
  liabilityPriceUSD?: number
  // Borrow validation props
  totalBorrowed?: NormalizedUnitNumber
  borrowCap?: NormalizedUnitNumber
  availableLiquidity?: NormalizedUnitNumber
  // Supply cap validation props (collateral vault)
  /** maxDeposit() remaining capacity — the canonical way to check deposit limits */
  collateralMaxDeposit?: NormalizedUnitNumber
  // Optional callback for LTV changes
  onLtvChange?: (ltvPct: number) => void
  /** When provided, use this subAccountId for operations (modifying existing position) */
  selectedSubAccountId?: number
  /** Existing position collateral value in USD (for showing current LTV) */
  existingCollateralUsd?: number
  /** Existing position debt value in USD (for showing current LTV) */
  existingDebtUsd?: number
  /** Controlled collateral amount from parent */
  controlledCollateralAmount?: string
  /** Controlled collateral amount change handler */
  onControlledCollateralAmountChange?: (value: string) => void
  /** Controlled borrow amount from parent */
  controlledBorrowAmount?: string
  /** Controlled borrow amount change handler */
  onControlledBorrowAmountChange?: (value: string) => void
  /** Existing controller to disable before enabling new one (for controller switching) */
  existingController?: Address
  /** Controlled: use native HYPE for collateral (wraps on deposit) */
  controlledUseNativeCollateral?: boolean
  /** Controlled: receive native HYPE for borrow (unwraps on receive) */
  controlledReceiveNativeBorrow?: boolean
  /** Controlled active tab */
  controlledActiveTab?: 'Borrow' | 'Multiply'
  /** Controlled active tab change handler */
  onControlledActiveTabChange?: (tab: 'Borrow' | 'Multiply') => void
  /** If true, show tabs even in controlled mode (allows toggling strategy) */
  showTabsWhenControlled?: boolean
  /** Route type: 'euler' supports Borrow+Multiply, 'pooled' supports Borrow only */
  routeType?: 'euler' | 'pooled'
}

const tabs = ['Borrow', 'Multiply'] as const
const borrowOnlyTabs = ['Borrow'] as const
type TabType = (typeof tabs)[number]

interface BorrowFormData {
  collateralAmount: string
  borrowAmount: string
  ltv: number
  isMaxCollateral: boolean
  isMaxBorrow: boolean
  /** 0 = native HYPE (if supported), 1 = WHYPE */
  collateralAssetIndex: number
  /** true = receive native HYPE, false = receive WHYPE */
  receiveNativeHype: boolean
}

export function BorrowMultiplyTabs({
  collateral,
  liability,
  collateralSymbol = 'Unknown',
  liabilitySymbol = 'Unknown',
  maxLTV,
  liquidationLTV,
  borrowAPY,
  supplyAPY,
  collateralIntrinsicAPY,
  collateralLendingAPY,
  evcAddress,
  collateralPriceUSD = 1,
  liabilityPriceUSD = 1,
  totalBorrowed,
  borrowCap,
  availableLiquidity,
  collateralMaxDeposit,
  onLtvChange: _onLtvChange, // Reserved for future use
  selectedSubAccountId,
  existingCollateralUsd = 0,
  existingDebtUsd = 0,
  controlledCollateralAmount,
  onControlledCollateralAmountChange,
  controlledBorrowAmount,
  onControlledBorrowAmountChange,
  existingController,
  controlledUseNativeCollateral,
  controlledReceiveNativeBorrow,
  controlledActiveTab,
  onControlledActiveTabChange,
  showTabsWhenControlled = false,
  routeType = 'euler',
}: BorrowMultiplyTabsProps) {
  // Check if we're in controlled mode (inputs are in parent component)
  const isControlled = controlledCollateralAmount !== undefined || controlledBorrowAmount !== undefined
  const [internalActiveTab, setInternalActiveTab] = useState<TabType>('Borrow')
  // For pooled routes, always use 'Borrow' tab (no multiply support)
  const activeTab = routeType === 'pooled' ? 'Borrow' : controlledActiveTab ?? internalActiveTab
  const setActiveTab = onControlledActiveTabChange ?? setInternalActiveTab
  // Pooled routes only support Borrow
  const availableTabs = routeType === 'pooled' ? borrowOnlyTabs : tabs

  const [slippage, setSlippage] = useState<Percentage>(Percentage(0.005)) // 0.5% default
  const { address: userAddress } = useAccount()

  // Fetch underlying asset for collateral vault
  const { data: collateralAsset } = useReadContract({
    address: collateral,
    abi: eVaultImplementationAbi,
    functionName: 'asset',
    chainId: BORROW_VAULT_CHAIN_ID,
  })

  // Fetch underlying asset for liability vault
  const { data: liabilityAsset } = useReadContract({
    address: liability,
    abi: eVaultImplementationAbi,
    functionName: 'asset',
    chainId: BORROW_VAULT_CHAIN_ID,
  })

  // Fetch collateral asset details
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const collateralContracts: any[] = [
    {
      address: collateralAsset as Address,
      abi: erc20Abi,
      functionName: 'symbol',
      chainId: BORROW_VAULT_CHAIN_ID,
    },
    {
      address: collateralAsset as Address,
      abi: erc20Abi,
      functionName: 'decimals',
      chainId: BORROW_VAULT_CHAIN_ID,
    },
    {
      address: collateralAsset as Address,
      abi: erc20Abi,
      functionName: 'name',
      chainId: BORROW_VAULT_CHAIN_ID,
    },
    ...(userAddress
      ? [
          {
            address: collateralAsset as Address,
            abi: erc20Abi,
            functionName: 'balanceOf',
            args: [userAddress],
            chainId: BORROW_VAULT_CHAIN_ID,
          },
        ]
      : []),
  ]

  const { data: collateralTokenData } = useReadContracts({
    contracts: collateralContracts,
    query: {
      enabled: !!collateralAsset,
    },
  })

  // Fetch liability asset details
  const { data: liabilityTokenData } = useReadContracts({
    contracts: [
      {
        address: liabilityAsset as Address,
        abi: erc20Abi,
        functionName: 'symbol',
        chainId: BORROW_VAULT_CHAIN_ID,
      },
      {
        address: liabilityAsset as Address,
        abi: erc20Abi,
        functionName: 'decimals',
        chainId: BORROW_VAULT_CHAIN_ID,
      },
      {
        address: liabilityAsset as Address,
        abi: erc20Abi,
        functionName: 'name',
        chainId: BORROW_VAULT_CHAIN_ID,
      },
    ],
    query: {
      enabled: !!liabilityAsset,
    },
  })

  const collateralTokenSymbol = (collateralTokenData?.[0]?.result as unknown as string) || collateralSymbol
  const collateralTokenDecimals = (collateralTokenData?.[1]?.result as unknown as number) || 18
  const collateralTokenName = (collateralTokenData?.[2]?.result as unknown as string) || collateralSymbol
  const collateralBalance = collateralTokenData?.[3]?.result as bigint | undefined

  const liabilityTokenSymbol = (liabilityTokenData?.[0]?.result as string) || liabilitySymbol
  const liabilityTokenDecimals = (liabilityTokenData?.[1]?.result as number) || 18
  const liabilityTokenName = (liabilityTokenData?.[2]?.result as string) || liabilitySymbol

  // Fetch token info from Euler indexer for proper icons
  const tokenAddresses = useMemo(() => {
    const addresses: Address[] = []
    if (collateralAsset) addresses.push(collateralAsset)
    if (liabilityAsset) addresses.push(liabilityAsset)
    return addresses
  }, [collateralAsset, liabilityAsset])

  const { tokens: tokenInfoMap } = useTokensInfo(BORROW_VAULT_CHAIN_ID, tokenAddresses)

  const collateralTokenInfo = getTokenFromMap(tokenInfoMap, collateralAsset)
  const liabilityTokenInfo = getTokenFromMap(tokenInfoMap, liabilityAsset)

  // Create tokens with logoURI from indexer and actual prices
  const collateralToken = collateralAsset
    ? new Token({
        symbol: TokenSymbol(collateralTokenSymbol),
        name: collateralTokenName,
        decimals: collateralTokenDecimals,
        address: CheckedAddress(collateralAsset),
        unitPriceUsd: String(collateralPriceUSD),
        logoURI: collateralTokenInfo?.logoURI,
      })
    : undefined

  const liabilityToken = liabilityAsset
    ? new Token({
        symbol: TokenSymbol(liabilityTokenSymbol),
        name: liabilityTokenName,
        decimals: liabilityTokenDecimals,
        address: CheckedAddress(liabilityAsset),
        unitPriceUsd: String(liabilityPriceUSD),
        logoURI: liabilityTokenInfo?.logoURI,
      })
    : undefined

  const collateralBalanceFormatted = collateralBalance
    ? NormalizedUnitNumber(formatUnits(collateralBalance, collateralTokenDecimals))
    : NormalizedUnitNumber('0')

  // LTV is stored in basis points (e.g., 9000 = 90%)
  // If maxLTV or liquidationLTV are missing, we cannot safely allow borrowing
  const hasLtvData = maxLTV !== undefined && liquidationLTV !== undefined
  const maxLTVPercent = maxLTV ? Number(maxLTV) / 100 : 0
  // Liquidation LTV (LLTV) is higher than max LTV - this is where liquidation happens
  const liquidationLTVPercent = liquidationLTV ? Number(liquidationLTV) / 100 : 0
  // Safety buffer to prevent edge cases with floating point math - never allow slider to reach exact max LTV
  const LTV_SAFETY_BUFFER = 0.01 // 0.01%
  const effectiveMaxLTVPercent = Math.max(0, maxLTVPercent - LTV_SAFETY_BUFFER)

  // Check if collateral/liability vaults support native HYPE (underlying is WHYPE)
  const supportsNativeCollateral = useMemo(
    () => (collateralAsset ? isWhypeVault(collateralAsset, BORROW_VAULT_CHAIN_ID) : false),
    [collateralAsset],
  )

  const supportsNativeBorrow = useMemo(
    () => (liabilityAsset ? isWhypeVault(liabilityAsset, BORROW_VAULT_CHAIN_ID) : false),
    [liabilityAsset],
  )

  // Fetch native HYPE balance
  const { data: nativeBalanceData } = useBalance({
    address: userAddress,
    chainId: BORROW_VAULT_CHAIN_ID,
    query: {
      enabled: !!userAddress && (supportsNativeCollateral || supportsNativeBorrow),
    },
  })

  const nativeBalance = nativeBalanceData
    ? NormalizedUnitNumber(formatUnits(nativeBalanceData.value, 18))
    : NormalizedUnitNumber('0')

  // Reserve some HYPE for gas costs when using native HYPE
  const nativeBalanceAfterGasReserve = NormalizedUnitNumber(
    Math.max(0, Number(nativeBalance) - NATIVE_HYPE_GAS_RESERVE).toString(),
  )

  // Create native HYPE token if supported
  const nativeHypeToken = useMemo(
    () =>
      supportsNativeCollateral || supportsNativeBorrow ? createNativeHypeToken(String(collateralPriceUSD)) : undefined,
    [supportsNativeCollateral, supportsNativeBorrow, collateralPriceUSD],
  )

  // When in controlled mode (inputs in parent), just show BorrowSection directly without tabs unless showTabsWhenControlled is true
  return (
    <Panel.Wrapper className="p-6">
      {(!isControlled || showTabsWhenControlled) && availableTabs.length > 1 && (
        <div className="mb-6 flex items-center justify-between">
          <TabSelector activeTab={activeTab} setActiveTab={setActiveTab} tabs={availableTabs} />
          {activeTab === 'Multiply' && (
            <SlippagePanel slippage={slippage} setSlippage={setSlippage} title="Swap Slippage" />
          )}
        </div>
      )}

      {activeTab === 'Borrow' ? (
        <BorrowSection
          collateralToken={collateralToken}
          liabilityToken={liabilityToken}
          collateralBalance={collateralBalanceFormatted}
          maxLTVPercent={effectiveMaxLTVPercent}
          liquidationLTVPercent={liquidationLTVPercent}
          hasLtvData={hasLtvData}
          borrowAPY={borrowAPY}
          supplyAPY={supplyAPY}
          collateralIntrinsicAPY={collateralIntrinsicAPY}
          collateralLendingAPY={collateralLendingAPY}
          userAddress={userAddress}
          collateralVault={collateral}
          liabilityVault={liability}
          collateralAsset={collateralAsset as Address}
          liabilityAsset={liabilityAsset as Address}
          evcAddress={evcAddress}
          collateralPriceUSD={collateralPriceUSD}
          liabilityPriceUSD={liabilityPriceUSD}
          collateralSymbol={collateralSymbol}
          totalBorrowed={totalBorrowed}
          borrowCap={borrowCap}
          availableLiquidity={availableLiquidity}
          collateralMaxDeposit={collateralMaxDeposit}
          supportsNativeCollateral={supportsNativeCollateral}
          supportsNativeBorrow={supportsNativeBorrow}
          nativeHypeToken={nativeHypeToken}
          nativeBalance={nativeBalanceAfterGasReserve}
          selectedSubAccountId={selectedSubAccountId}
          existingCollateralUsd={existingCollateralUsd}
          existingDebtUsd={existingDebtUsd}
          controlledCollateralAmount={controlledCollateralAmount}
          onControlledCollateralAmountChange={onControlledCollateralAmountChange}
          controlledBorrowAmount={controlledBorrowAmount}
          onControlledBorrowAmountChange={onControlledBorrowAmountChange}
          existingController={existingController}
          controlledUseNativeCollateral={controlledUseNativeCollateral}
          controlledReceiveNativeBorrow={controlledReceiveNativeBorrow}
        />
      ) : (
        <MultiplySection
          collateralToken={collateralToken}
          liabilityToken={liabilityToken}
          collateralBalance={collateralBalanceFormatted}
          maxLTVPercent={effectiveMaxLTVPercent}
          liquidationLTVPercent={liquidationLTVPercent}
          hasLtvData={hasLtvData}
          borrowAPY={borrowAPY}
          supplyAPY={supplyAPY}
          collateralIntrinsicAPY={collateralIntrinsicAPY}
          collateralLendingAPY={collateralLendingAPY}
          userAddress={userAddress}
          collateralVault={collateral}
          liabilityVault={liability}
          collateralAsset={collateralAsset as Address}
          liabilityAsset={liabilityAsset as Address}
          evcAddress={evcAddress}
          collateralPriceUSD={collateralPriceUSD}
          liabilityPriceUSD={liabilityPriceUSD}
          collateralSymbol={collateralSymbol}
          totalBorrowed={totalBorrowed}
          borrowCap={borrowCap}
          availableLiquidity={availableLiquidity}
          collateralMaxDeposit={collateralMaxDeposit}
          supportsNativeCollateral={supportsNativeCollateral}
          supportsNativeBorrow={supportsNativeBorrow}
          nativeHypeToken={nativeHypeToken}
          nativeBalance={nativeBalanceAfterGasReserve}
          slippage={slippage}
          selectedSubAccountId={selectedSubAccountId}
          existingCollateralUsd={existingCollateralUsd}
          existingDebtUsd={existingDebtUsd}
          // Controlled mode props for Multiply
          controlledCollateralAmount={controlledCollateralAmount}
          onControlledCollateralAmountChange={onControlledCollateralAmountChange}
          controlledBorrowAmount={controlledBorrowAmount}
          onControlledBorrowAmountChange={onControlledBorrowAmountChange}
          controlledUseNativeCollateral={controlledUseNativeCollateral}
        />
      )}
    </Panel.Wrapper>
  )
}

interface SectionProps {
  collateralToken?: Token
  liabilityToken?: Token
  collateralBalance: NormalizedUnitNumber
  maxLTVPercent: number
  liquidationLTVPercent: number
  hasLtvData: boolean
  borrowAPY?: number
  /** Collateral supply APY (used for ROE calculation in Position Summary) */
  supplyAPY?: number
  /** Intrinsic APY of the collateral asset (e.g. staking yield) — from indexer */
  collateralIntrinsicAPY?: number
  /** Lending APY of the collateral vault (interest from borrowers) — from indexer */
  collateralLendingAPY?: number
  userAddress?: Address
  collateralVault: Address
  liabilityVault: Address
  collateralAsset?: Address
  liabilityAsset?: Address
  evcAddress?: Address
  collateralPriceUSD: number
  liabilityPriceUSD: number
  // Symbol fallback for validation messages
  collateralSymbol?: string
  // Borrow validation props
  totalBorrowed?: NormalizedUnitNumber
  borrowCap?: NormalizedUnitNumber
  availableLiquidity?: NormalizedUnitNumber
  // Supply cap validation via ERC4626 maxDeposit (collateral vault)
  collateralMaxDeposit?: NormalizedUnitNumber
  // Native HYPE support
  supportsNativeCollateral?: boolean
  supportsNativeBorrow?: boolean
  nativeHypeToken?: Token
  nativeBalance?: NormalizedUnitNumber
  // Slippage for swaps
  slippage?: Percentage
  /** When provided, use this subAccountId for operations (modifying existing position) */
  selectedSubAccountId?: number
  /** Existing position collateral value in USD (for showing current LTV) */
  existingCollateralUsd?: number
  /** Existing controller to disable before enabling new one (for controller switching) */
  existingController?: Address
  /** Existing position debt value in USD (for showing current LTV) */
  existingDebtUsd?: number
  // Controlled mode props (inputs are in parent component)
  /** Controlled collateral amount from parent */
  controlledCollateralAmount?: string
  /** Controlled collateral amount change handler */
  onControlledCollateralAmountChange?: (value: string) => void
  /** Controlled borrow amount from parent */
  controlledBorrowAmount?: string
  /** Controlled borrow amount change handler */
  onControlledBorrowAmountChange?: (value: string) => void
  /** Controlled: use native HYPE for collateral (wraps on deposit) */
  controlledUseNativeCollateral?: boolean
  /** Controlled: receive native HYPE for borrow (unwraps on receive) */
  controlledReceiveNativeBorrow?: boolean
}

function BorrowSection({
  collateralToken,
  liabilityToken,
  collateralBalance,
  maxLTVPercent,
  liquidationLTVPercent,
  hasLtvData,
  borrowAPY,
  supplyAPY,
  collateralIntrinsicAPY,
  collateralLendingAPY,
  userAddress,
  collateralVault,
  liabilityVault,
  collateralAsset,
  liabilityAsset,
  evcAddress,
  collateralPriceUSD,
  liabilityPriceUSD,
  collateralSymbol,
  totalBorrowed,
  borrowCap,
  availableLiquidity,
  collateralMaxDeposit,
  supportsNativeCollateral,
  supportsNativeBorrow,
  nativeHypeToken,
  nativeBalance,
  selectedSubAccountId,
  existingCollateralUsd = 0,
  existingDebtUsd = 0,
  controlledCollateralAmount,
  controlledBorrowAmount,
  onControlledBorrowAmountChange,
  existingController,
  controlledUseNativeCollateral,
  controlledReceiveNativeBorrow,
}: SectionProps) {
  // Check if we're in controlled mode (inputs are in parent component)
  const isControlled = controlledCollateralAmount !== undefined || controlledBorrowAmount !== undefined
  const openDialog = useOpenDialog()
  const { connectWallet } = usePrivy()

  // Find available sub-account for this liability vault
  const {
    availableSubAccountId,
    hasExistingPosition,
    existingSubAccountId,
    allSubAccountsUsed,
    isLoading: isSubAccountLoading,
  } = useAvailableSubAccount({
    liabilityVault,
    chainId: BORROW_VAULT_CHAIN_ID,
  })

  // Use selectedSubAccountId if provided (user selected a specific position),
  // otherwise fall back to auto-detection (existing position or available one)
  const subAccountId =
    selectedSubAccountId !== undefined
      ? selectedSubAccountId
      : hasExistingPosition
        ? existingSubAccountId
        : availableSubAccountId

  // Calculate existing position LTV and available borrow room
  const hasExistingPositionData = existingCollateralUsd > 0
  const currentLtvPercent = hasExistingPositionData ? (existingDebtUsd / existingCollateralUsd) * 100 : 0

  // Get the sub-account address for fetching enabled collaterals
  const positionAddress = useMemo(() => {
    if (!userAddress || subAccountId === null) return undefined
    return getSubAccountAddress(userAddress, subAccountId)
  }, [userAddress, subAccountId])

  // Fetch enabled collaterals for this position (needed to skip enableCollateral for already-enabled vaults)
  const { data: enabledCollateralsData } = useReadContract({
    address: evcAddress,
    abi: eulerVaultConnectorAbi,
    functionName: 'getCollaterals',
    args: positionAddress ? [positionAddress] : undefined,
    chainId: BORROW_VAULT_CHAIN_ID,
    query: {
      enabled: !!evcAddress && !!positionAddress && hasExistingPositionData,
      staleTime: 30_000,
    },
  })

  const enabledCollaterals = (enabledCollateralsData as Address[] | undefined) || []

  const form = useForm<BorrowFormData>({
    defaultValues: {
      collateralAmount: '',
      borrowAmount: '',
      ltv: 0,
      isMaxCollateral: false,
      isMaxBorrow: false,
      // Default to the asset with higher balance (native HYPE or WHYPE)
      collateralAssetIndex:
        supportsNativeCollateral && nativeBalance && Number(nativeBalance) > Number(collateralBalance) ? 0 : 1,
      receiveNativeHype: supportsNativeBorrow ?? false, // Default to native HYPE if supported
    },
  })

  const collateralAmount = form.watch('collateralAmount')
  const borrowAmount = form.watch('borrowAmount')
  const normalizedBorrowAmount = borrowAmount.trim() === '' ? '0' : borrowAmount
  const ltv = form.watch('ltv')
  const collateralAssetIndex = form.watch('collateralAssetIndex')
  const receiveNativeHype = form.watch('receiveNativeHype')

  // Track if user has manually changed asset selection (to prevent auto-switching)
  const [hasUserChangedAsset, setHasUserChangedAsset] = useState(false)

  // Sync controlled collateral amount from parent to form (one-way: parent → form)
  // biome-ignore lint/correctness/useExhaustiveDependencies: collateralAmount intentionally excluded to prevent infinite loop
  useEffect(() => {
    if (controlledCollateralAmount !== undefined && controlledCollateralAmount !== collateralAmount) {
      form.setValue('collateralAmount', controlledCollateralAmount, { shouldValidate: false })
    }
  }, [controlledCollateralAmount, form])

  // Sync controlled borrow amount from parent to form (one-way: parent → form)
  // biome-ignore lint/correctness/useExhaustiveDependencies: controlledBorrowAmount intentionally excluded to prevent infinite loop
  useEffect(() => {
    if (controlledBorrowAmount !== undefined && controlledBorrowAmount !== borrowAmount) {
      form.setValue('borrowAmount', controlledBorrowAmount, { shouldValidate: false })
    }
  }, [controlledBorrowAmount, form])

  // Update collateral asset selection when balances load (only if user hasn't manually changed)
  useEffect(() => {
    if (!hasUserChangedAsset && supportsNativeCollateral && nativeBalance && collateralBalance) {
      const shouldSelectNative = Number(nativeBalance) > Number(collateralBalance)
      const currentIndex = form.getValues('collateralAssetIndex')
      const expectedIndex = shouldSelectNative ? 0 : 1

      // Only update if the balance-based selection differs from current
      if (currentIndex !== expectedIndex) {
        form.setValue('collateralAssetIndex', expectedIndex)
      }
    }
  }, [hasUserChangedAsset, supportsNativeCollateral, nativeBalance, collateralBalance, form])

  // Determine if using native HYPE for collateral
  // In controlled mode (Quick Borrow), use the controlled value from parent
  // Otherwise, use the form's collateralAssetIndex (0 = native, 1 = wrapped)
  const isNativeCollateral = isControlled
    ? (controlledUseNativeCollateral ?? false) && supportsNativeCollateral
    : supportsNativeCollateral && collateralAssetIndex === 0

  // Determine if receiving native HYPE for borrow
  // In controlled mode, use the controlled value from parent
  const shouldReceiveNativeHype = isControlled
    ? (controlledReceiveNativeBorrow ?? false) && supportsNativeBorrow
    : receiveNativeHype && supportsNativeBorrow

  // Build collateral asset options
  const collateralAssets = useMemo(() => {
    const assets: { token: Token; balance: NormalizedUnitNumber }[] = []

    // Native HYPE first if supported (gas reserve already applied to nativeBalance prop)
    if (supportsNativeCollateral && nativeHypeToken && nativeBalance) {
      assets.push({ token: nativeHypeToken, balance: nativeBalance })
    }

    // WHYPE/underlying token
    if (collateralToken) {
      assets.push({ token: collateralToken, balance: collateralBalance })
    }

    return assets
  }, [supportsNativeCollateral, nativeHypeToken, nativeBalance, collateralToken, collateralBalance])

  // Get selected collateral token and balance
  // In controlled mode, use isNativeCollateral to determine the correct token/balance
  // In non-controlled mode, use collateralAssetIndex from the form
  const selectedCollateralToken = isControlled
    ? isNativeCollateral && nativeHypeToken
      ? nativeHypeToken
      : collateralToken
    : collateralAssets[collateralAssetIndex]?.token ?? collateralToken
  const selectedCollateralBalance = isControlled
    ? isNativeCollateral && nativeBalance
      ? nativeBalance
      : collateralBalance
    : collateralAssets[collateralAssetIndex]?.balance ?? collateralBalance

  // Get selected borrow token (for display)
  const selectedBorrowToken = shouldReceiveNativeHype && nativeHypeToken ? nativeHypeToken : liabilityToken

  // Build borrow asset options (for native HYPE unwrap support)
  const borrowAssets = useMemo(() => {
    const assets: { token: Token; balance: NormalizedUnitNumber }[] = []

    // Liability token (e.g., WHYPE, USDC)
    if (liabilityToken) {
      assets.push({ token: liabilityToken, balance: availableLiquidity || NormalizedUnitNumber('0') })
    }

    // Native HYPE option if liability vault supports it (underlying is WHYPE)
    if (supportsNativeBorrow && nativeHypeToken) {
      assets.push({ token: nativeHypeToken, balance: availableLiquidity || NormalizedUnitNumber('0') })
    }

    return assets
  }, [liabilityToken, supportsNativeBorrow, nativeHypeToken, availableLiquidity])

  // Calculate collateral values for LTV calculations (needed for maxBorrow)
  // Includes both new collateral being added AND existing collateral in position
  const newCollateralValueUsd =
    collateralAmount && Number(collateralAmount) > 0 ? Number(collateralAmount) * collateralPriceUSD : 0
  const totalCollateralValueUsd = existingCollateralUsd + newCollateralValueUsd

  // Calculate max borrow based on total collateral (existing + new), LTV, and existing debt
  // maxDebtUsd = totalCollateralValueUsd * maxLTVPercent / 100
  // maxNewBorrowUsd = maxDebtUsd - existingDebtUsd
  // maxBorrowTokens = maxNewBorrowUsd / liabilityPriceUSD
  const maxBorrowValue =
    totalCollateralValueUsd > 0 && liabilityPriceUSD > 0
      ? Math.max(0, (totalCollateralValueUsd * maxLTVPercent) / 100 - existingDebtUsd) / liabilityPriceUSD
      : 0
  const maxBorrow = NormalizedUnitNumber(maxBorrowValue.toString())

  // Validate borrow against cap and liquidity
  const borrowValidation = useMemo(() => {
    return validateEulerBorrow({
      value: NormalizedUnitNumber(normalizedBorrowAmount),
      totalBorrowed: totalBorrowed || NormalizedUnitNumber('0'),
      borrowCap,
      availableLiquidity: availableLiquidity || NormalizedUnitNumber('0'),
      symbol: liabilityToken?.symbol,
    })
  }, [normalizedBorrowAmount, totalBorrowed, borrowCap, availableLiquidity, liabilityToken?.symbol])

  // Validate collateral deposit against supply cap / maxDeposit
  const supplyValidation = useMemo(() => {
    return validateMaxDeposit({
      depositAmount: collateralAmount,
      maxDeposit: collateralMaxDeposit,
      symbol: selectedCollateralToken?.symbol || collateralSymbol,
    })
  }, [collateralAmount, collateralMaxDeposit, selectedCollateralToken?.symbol, collateralSymbol])

  // Preview APY: parse amounts to bigint for on-chain simulation
  const collateralAmountBigInt = useMemo(() => {
    if (!collateralAmount || !collateralToken) return 0n
    try {
      return parseUnits(collateralAmount, collateralToken.decimals)
    } catch {
      return 0n
    }
  }, [collateralAmount, collateralToken])

  const borrowAmountBigInt = useMemo(() => {
    if (!borrowAmount || !liabilityToken) return 0n
    try {
      return parseUnits(borrowAmount, liabilityToken.decimals)
    } catch {
      return 0n
    }
  }, [borrowAmount, liabilityToken])

  const { newAPY: supplyPreviewNew, isLoading: isSupplyPreviewLoading } = usePreviewSupplyAPY({
    vaultAddress: collateralVault,
    changeAmount: collateralAmountBigInt,
    changeType: 'deposit',
    chainId: BORROW_VAULT_CHAIN_ID,
    enabled: collateralAmountBigInt > 0n,
  })

  const { newBorrowAPY: borrowPreviewNew, isLoading: isBorrowPreviewLoading } = usePreviewSupplyAPY({
    vaultAddress: liabilityVault,
    changeAmount: borrowAmountBigInt,
    changeType: 'borrow',
    chainId: BORROW_VAULT_CHAIN_ID,
    enabled: borrowAmountBigInt > 0n,
  })

  // Auto-update borrow amount when collateral changes (if LTV > 0)
  // Set a default LTV of 50% when user first enters collateral
  // Skip this in controlled mode - parent manages both amounts
  const prevCollateralRef = useRef('')
  // biome-ignore lint/correctness/useExhaustiveDependencies: ltv intentionally excluded to prevent infinite loop
  useEffect(() => {
    // Skip auto-update when in controlled mode - parent manages amounts
    if (isControlled) return

    if (collateralAmount && Number(collateralAmount) > 0 && liabilityPriceUSD > 0) {
      // If collateral changed and LTV is 0, set a default LTV
      let currentLTV = ltv
      if (prevCollateralRef.current === '' && ltv === 0) {
        currentLTV = Math.min(50, maxLTVPercent) // Default to 50% or max LTV
        form.setValue('ltv', currentLTV)
      }
      prevCollateralRef.current = collateralAmount

      // Update borrow amount based on current LTV
      if (currentLTV > 0) {
        const collateralValueUSD = Number(collateralAmount) * collateralPriceUSD
        const maxBorrowValueUSD = (collateralValueUSD * currentLTV) / 100
        const newBorrowAmount = maxBorrowValueUSD / liabilityPriceUSD
        form.setValue('borrowAmount', newBorrowAmount.toFixed(6))
      }
    } else if (!collateralAmount || Number(collateralAmount) <= 0) {
      // Clear borrow amount and reset LTV when collateral is empty
      form.setValue('borrowAmount', '')
      form.setValue('ltv', 0)
      form.setValue('isMaxBorrow', false)
      prevCollateralRef.current = ''
    }
  }, [collateralAmount, collateralPriceUSD, liabilityPriceUSD, form, maxLTVPercent, isControlled])

  // Calculate final LTV after new collateral + borrow
  const newBorrowValueUsd = borrowAmount && Number(borrowAmount) > 0 ? Number(borrowAmount) * liabilityPriceUSD : 0
  const finalDebtUsd = existingDebtUsd + newBorrowValueUsd
  const finalLtvPercent = totalCollateralValueUsd > 0 ? (finalDebtUsd / totalCollateralValueUsd) * 100 : 0

  // When maxLTV changes (route switch), adjust borrow amount if it exceeds new max
  const prevMaxLtvRef = useRef(maxLTVPercent)

  // biome-ignore lint/correctness/useExhaustiveDependencies: maxLTVPercent intentionally excluded to prevent infinite loop
  useEffect(() => {
    // Only run when maxLTV actually changes
    if (prevMaxLtvRef.current === maxLTVPercent) return
    prevMaxLtvRef.current = maxLTVPercent

    // If we have collateral and the current LTV exceeds the new max, adjust borrow to fit
    if (isControlled && totalCollateralValueUsd > 0 && finalLtvPercent > maxLTVPercent && liabilityPriceUSD > 0) {
      // Calculate max borrow at new maxLTV
      const maxDebtUsd = (totalCollateralValueUsd * maxLTVPercent) / 100
      const maxNewBorrowUsd = Math.max(0, maxDebtUsd - existingDebtUsd)
      const maxBorrowAmount = maxNewBorrowUsd / liabilityPriceUSD
      const maxBorrowAmountStr = maxBorrowAmount > 0 ? maxBorrowAmount.toFixed(6) : ''

      form.setValue('borrowAmount', maxBorrowAmountStr)
      if (onControlledBorrowAmountChange) {
        onControlledBorrowAmountChange(maxBorrowAmountStr)
      }
    }
  }, [maxLTVPercent])

  // Determine if we're modifying an existing position (for button text and dialog title)
  const hasCollateral = collateralAmount && Number(collateralAmount) > 0
  const hasBorrow = borrowAmount && Number(borrowAmount) > 0
  const isModifyingExistingPosition = hasExistingPositionData && selectedSubAccountId !== undefined

  const handleOpenPosition = form.handleSubmit(() => {
    if (!selectedCollateralToken || !collateralAsset || !selectedBorrowToken || !liabilityAsset) return

    // Use new EVC borrow dialog which uses Euler's full wagmi-evc stack
    // for proper simulation, debug tracing, and transaction execution
    openDialog(evcBorrowDialogConfig, {
      collateralVault,
      collateralSymbol: String(selectedCollateralToken.symbol),
      collateralAmount,
      collateralDecimals: selectedCollateralToken.decimals,
      collateralAsset,
      ...(selectedCollateralToken.logoURI && { collateralLogoURI: selectedCollateralToken.logoURI }),
      liabilityVault,
      liabilitySymbol: String(selectedBorrowToken.symbol),
      borrowAmount: normalizedBorrowAmount,
      liabilityDecimals: selectedBorrowToken.decimals,
      liabilityAsset,
      ...(selectedBorrowToken.logoURI && { liabilityLogoURI: selectedBorrowToken.logoURI }),
      ...(subAccountId !== null && { subAccountId }),
      // Native HYPE flags
      ...(isNativeCollateral && { wrapCollateralFromNative: true }),
      ...(shouldReceiveNativeHype && { unwrapBorrowToNative: true }),
      // Controller switching - disable existing controller before enabling new one
      ...(existingController && { existingController }),
      // Pass whether we're modifying an existing position for dialog title
      ...(isModifyingExistingPosition && { isModifyingExistingPosition: true }),
      // Pass enabled collaterals so we only skip enableCollateral for vaults that are already enabled
      ...(enabledCollaterals.length > 0 && { enabledCollaterals }),
    })
  })

  // For new positions: can't borrow without collateral
  // For existing positions: can add collateral only OR borrow more (if position already has collateral)
  const hasRequiredInputs = isModifyingExistingPosition
    ? hasCollateral || hasBorrow // Existing position: can add collateral only or borrow more
    : hasBorrow
      ? hasCollateral // New position with borrow: must have collateral
      : hasCollateral // New position without borrow: must have collateral

  const canOpenPosition =
    hasLtvData && // LTV data must be available
    hasRequiredInputs &&
    (!hasCollateral || Number(collateralAmount) <= Number(selectedCollateralBalance)) &&
    evcAddress &&
    borrowValidation.canProceed &&
    supplyValidation.canProceed &&
    (isModifyingExistingPosition || !allSubAccountsUsed) && // Skip sub-account check when modifying existing position
    finalLtvPercent <= maxLTVPercent && // Don't exceed max LTV
    !isSubAccountLoading // Wait for subaccount discovery to complete

  return (
    <FormProvider {...form}>
      <form onSubmit={handleOpenPosition} className="flex flex-col gap-4">
        {/* Collateral Input - hide in controlled mode (quick borrow has its own) */}
        {!isControlled && (
          <div className="flex flex-col gap-2">
            <label className="text-muted-foreground text-xs">Collateral</label>
            <div className="flex gap-2">
              {selectedCollateralToken && (
                <>
                  <ControlledMultiSelectorAssetInput
                    fieldName="collateralAmount"
                    control={form.control}
                    token={selectedCollateralToken}
                    max={selectedCollateralBalance}
                    maxSelectedFieldName="isMaxCollateral"
                    balance={selectedCollateralBalance}
                    balanceType="wallet"
                    disabled={!userAddress}
                  />
                  <AssetSelector
                    assets={collateralAssets}
                    selectedAsset={selectedCollateralToken}
                    setSelectedAsset={(symbol) => {
                      const idx = collateralAssets.findIndex((a) => a.token.symbol === symbol)
                      if (idx >= 0) {
                        form.setValue('collateralAssetIndex', idx)
                        form.setValue('collateralAmount', '') // Reset amount when switching
                        form.setValue('borrowAmount', '') // Reset borrow amount when switching collateral
                        form.setValue('isMaxCollateral', false) // Reset max button state
                        form.setValue('isMaxBorrow', false) // Reset max button state
                        setHasUserChangedAsset(true) // Mark that user manually changed selection
                      }
                    }}
                    disabled={collateralAssets.length <= 1}
                  />
                </>
              )}
            </div>
          </div>
        )}

        {/* Borrow Input - hide in controlled mode (quick borrow has its own) */}
        {!isControlled && (
          <div className="flex flex-col gap-2">
            <label className="text-muted-foreground text-xs">Borrow</label>
            <div className="flex gap-2">
              {selectedBorrowToken && (
                <>
                  <ControlledMultiSelectorAssetInput
                    fieldName="borrowAmount"
                    control={form.control}
                    token={selectedBorrowToken}
                    max={maxBorrow}
                    maxSelectedFieldName="isMaxBorrow"
                    balance={maxBorrow}
                    balanceType="wallet"
                    disabled={!userAddress || !collateralAmount || Number(collateralAmount) <= 0}
                  />
                  <AssetSelector
                    assets={borrowAssets}
                    selectedAsset={selectedBorrowToken}
                    setSelectedAsset={(symbol) => {
                      // Toggle between native HYPE and wrapped token
                      const isNative = nativeHypeToken && symbol === nativeHypeToken.symbol
                      form.setValue('receiveNativeHype', !!isNative)
                    }}
                    disabled={borrowAssets.length <= 1}
                  />
                </>
              )}
            </div>
          </div>
        )}

        {/* Classic Mode: LTV + Slider + Button + Info Block (controlled mode / Quick Borrow) */}
        {isControlled ? (
          <>
            {/* LTV Display */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-base">Loan to Value (LTV)</span>
                <span
                  className={`font-semibold text-base ${finalLtvPercent > liquidationLTVPercent * 0.9 ? 'text-red-400' : finalLtvPercent > liquidationLTVPercent * 0.7 ? 'text-amber-400' : 'text-emerald-400'}`}
                >
                  {finalLtvPercent.toFixed(2)}%
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-sm">Max LTV</span>
                <span className="text-muted-foreground text-sm">max {maxLTVPercent.toFixed(2)}%</span>
              </div>
            </div>

            {/* Classic LTV Slider - same style as Multiply section */}
            <Slider
              className="mt-4"
              value={[Math.min(finalLtvPercent, maxLTVPercent)]}
              max={maxLTVPercent}
              step={0.1}
              onValueChange={(values) => {
                const targetLtvPercent = values[0] || 0
                // Calculate borrow amount needed to reach target LTV
                const targetDebtUsd = (targetLtvPercent * totalCollateralValueUsd) / 100
                const newBorrowUsd = Math.max(0, targetDebtUsd - existingDebtUsd)
                const newBorrowAmount = liabilityPriceUSD > 0 ? newBorrowUsd / liabilityPriceUSD : 0
                const newBorrowAmountStr = newBorrowAmount > 0 ? newBorrowAmount.toFixed(6) : ''
                form.setValue('borrowAmount', newBorrowAmountStr)
                // Notify parent so the input updates
                if (onControlledBorrowAmountChange) {
                  onControlledBorrowAmountChange(newBorrowAmountStr)
                }
              }}
              disabled={!userAddress || totalCollateralValueUsd <= 0}
              trackClassName="bg-gradient-to-r from-emerald-500 via-amber-500 via-70% to-red-500"
              fixedGradient
            />
            <div className="flex items-center justify-between text-white/30 text-xs">
              <span>0%</span>
              <span>
                Max: {maxLTVPercent.toFixed(1)}% · Liq: {liquidationLTVPercent.toFixed(1)}%
              </span>
            </div>

            {/* Button */}
            {!userAddress ? (
              <Button type="button" onClick={connectWallet} variant="primary" className="mt-4 w-full" rounded="full">
                Connect Wallet
              </Button>
            ) : (
              <>
                <Button
                  type="submit"
                  disabled={!canOpenPosition}
                  variant="primary"
                  className="mt-4 w-full"
                  rounded="full"
                >
                  {allSubAccountsUsed
                    ? 'Max Positions Reached'
                    : !hasLtvData
                      ? 'LTV Data Unavailable'
                      : isModifyingExistingPosition
                        ? hasBorrow && !hasCollateral
                          ? 'Borrow More'
                          : 'Add to Position'
                        : 'Continue'}
                </Button>
                {!hasLtvData && (
                  <div className="mt-2 rounded-lg border border-yellow-500/20 bg-yellow-500/10 p-3">
                    <p className="text-sm text-yellow-400">
                      Max LTV and Liquidation LTV data are not available for this vault pair. Opening a position is
                      currently disabled for safety.
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Validation errors - displayed under button */}
            <ValidationAlert validation={supplyValidation} />
            <ValidationAlert validation={borrowValidation} />

            <div className="mt-6 flex flex-col gap-2 rounded-xl border bg-card p-4">
              {/* New Collateral section - show when adding to existing position */}
              {hasExistingPositionData && newCollateralValueUsd > 0 && (
                <>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm text-white">New Collateral</span>
                    <div className="flex flex-col items-end">
                      <span className="font-medium text-sm text-white">
                        {Number(collateralAmount).toLocaleString(undefined, { maximumFractionDigits: 4 })}{' '}
                        {selectedCollateralToken?.symbol}
                      </span>
                      <span className="text-muted-foreground text-xs">
                        ${newCollateralValueUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground text-sm tracking-[-0.3px]">Current Collateral</span>
                    <span className="text-muted-foreground text-sm tracking-[-0.3px]">
                      ${existingCollateralUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm text-white">Total Collateral</span>
                    <span className="font-medium text-sm text-white">
                      ${totalCollateralValueUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="my-1 border-white/10 border-t" />
                </>
              )}
              {/* New Debt section */}
              {newBorrowValueUsd > 0 && (
                <>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-sm text-white">New Debt</span>
                    <div className="flex flex-col items-end">
                      <span className="font-medium text-sm text-white">
                        {Number(borrowAmount).toLocaleString(undefined, { maximumFractionDigits: 4 })}{' '}
                        {selectedBorrowToken?.symbol}
                      </span>
                      <span className="text-muted-foreground text-xs">
                        ${newBorrowValueUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>
                  {hasExistingPositionData && (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-muted-foreground text-sm tracking-[-0.3px]">Current Debt</span>
                        <span className="text-muted-foreground text-sm tracking-[-0.3px]">
                          ${existingDebtUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm text-white">Total Debt</span>
                        <span className="font-medium text-sm text-white">
                          ${finalDebtUsd.toLocaleString(undefined, { maximumFractionDigits: 2 })}
                        </span>
                      </div>
                    </>
                  )}
                  <div className="my-1 border-white/10 border-t" />
                </>
              )}
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-sm tracking-[-0.3px]">Borrow APY</span>
                <div className="flex items-center gap-1.5 text-sm tracking-[-0.3px]">
                  <span className="text-muted-foreground">
                    {borrowAPY !== undefined ? `${borrowAPY.toFixed(2)}%` : '-'}
                  </span>
                  {borrowPreviewNew !== undefined && borrowAmountBigInt > 0n && !isBorrowPreviewLoading && (
                    <ApyChange currentAPY={borrowAPY ?? 0} newAPY={borrowPreviewNew} type="cost" />
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-sm tracking-[-0.3px]">Supply APY</span>
                <div className="flex items-center gap-1.5 text-sm tracking-[-0.3px]">
                  <SupplyApyTooltip
                    breakdown={{
                      lendingApy: collateralLendingAPY ?? 0,
                      intrinsicApy: collateralIntrinsicAPY,
                      totalApy: supplyAPY ?? 0,
                    }}
                    showPointsSection={false}
                  >
                    <span className="text-muted-foreground">
                      {supplyAPY !== undefined ? `${supplyAPY.toFixed(2)}%` : '-'}
                    </span>
                  </SupplyApyTooltip>
                  {supplyPreviewNew !== undefined &&
                    collateralAmountBigInt > 0n &&
                    !isSupplyPreviewLoading &&
                    (() => {
                      const newTotal = addIntrinsicAPY(supplyPreviewNew, collateralIntrinsicAPY)
                      return <ApyChange currentAPY={supplyAPY ?? 0} newAPY={newTotal} type="earning" />
                    })()}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-sm tracking-[-0.3px]">Liquidation LTV</span>
                <span className="text-muted-foreground text-sm tracking-[-0.3px]">
                  {liquidationLTVPercent.toFixed(2)}%
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-sm tracking-[-0.3px]">Health Score</span>
                <span className="text-muted-foreground text-sm tracking-[-0.3px]">
                  {finalLtvPercent > 0
                    ? liquidationLTVPercent / finalLtvPercent > 100
                      ? '>100'
                      : (liquidationLTVPercent / finalLtvPercent).toFixed(2)
                    : '∞'}
                </span>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Standard Mode: LTV Slider + Info Block (standalone eBorrow page) */}
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <label className="text-muted-foreground text-xs">Position LTV</label>
                <div className="flex items-center gap-2">
                  {hasExistingPositionData && (
                    <>
                      <span className="text-muted-foreground text-xs">{currentLtvPercent.toFixed(1)}%</span>
                      <span className="text-muted-foreground text-xs">→</span>
                    </>
                  )}
                  <span
                    className={`font-medium text-xs ${finalLtvPercent > liquidationLTVPercent * 0.9 ? 'text-red-400' : finalLtvPercent > liquidationLTVPercent * 0.7 ? 'text-amber-400' : 'text-emerald-400'}`}
                  >
                    {finalLtvPercent.toFixed(1)}%
                  </span>
                </div>
              </div>
              <Slider
                value={[Math.min(finalLtvPercent, maxLTVPercent)]}
                max={maxLTVPercent}
                step={0.1}
                onValueChange={(values) => {
                  const targetLtv = values[0] || 0
                  // Calculate borrow amount needed to reach target LTV
                  // targetLtv = (existingDebt + newBorrow) / totalCollateral * 100
                  // newBorrow = (targetLtv * totalCollateral / 100) - existingDebt
                  const targetDebtUsd = (targetLtv * totalCollateralValueUsd) / 100
                  const newBorrowUsd = Math.max(0, targetDebtUsd - existingDebtUsd)
                  const newBorrowAmount = liabilityPriceUSD > 0 ? newBorrowUsd / liabilityPriceUSD : 0
                  const newBorrowAmountStr = newBorrowAmount > 0 ? newBorrowAmount.toFixed(6) : ''
                  form.setValue('borrowAmount', newBorrowAmountStr)
                }}
                disabled={!userAddress || totalCollateralValueUsd <= 0}
                trackClassName="bg-gradient-to-r from-emerald-500 via-amber-500 via-70% to-red-500"
                fixedGradient
              />
              <div className="flex items-center justify-between text-white/30 text-xs">
                <span>{hasExistingPositionData ? `Current: ${currentLtvPercent.toFixed(1)}%` : '0%'}</span>
                <span>
                  Max: {maxLTVPercent.toFixed(1)}% · Liq: {liquidationLTVPercent.toFixed(1)}%
                </span>
              </div>
            </div>

            {!userAddress ? (
              <Button type="button" onClick={connectWallet} variant="primary" className="w-full">
                Connect Wallet
              </Button>
            ) : (
              <>
                <Button type="submit" disabled={!canOpenPosition} variant="primary" className="w-full">
                  {allSubAccountsUsed
                    ? 'Max Positions Reached'
                    : !hasLtvData
                      ? 'LTV Data Unavailable'
                      : isModifyingExistingPosition
                        ? hasBorrow && !hasCollateral
                          ? 'Borrow More'
                          : 'Add to Position'
                        : 'Open Position'}
                </Button>
                {!hasLtvData && (
                  <div className="mt-2 rounded-lg border border-yellow-500/20 bg-yellow-500/10 p-3">
                    <p className="text-sm text-yellow-400">
                      Max LTV and Liquidation LTV data are not available for this vault pair. Opening a position is
                      currently disabled for safety.
                    </p>
                  </div>
                )}
              </>
            )}

            {/* Validation errors - displayed under button */}
            <ValidationAlert validation={supplyValidation} />
            <ValidationAlert validation={borrowValidation} />

            {/* Info block below button */}
            <div className="flex flex-col gap-2 rounded-xl border bg-card p-4">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-sm tracking-[-0.3px]">Borrow APY</span>
                <div className="flex items-center gap-1.5 text-sm tracking-[-0.3px]">
                  <span className="text-muted-foreground">
                    {borrowAPY !== undefined ? `${borrowAPY.toFixed(2)}%` : '-'}
                  </span>
                  {borrowPreviewNew !== undefined && borrowAmountBigInt > 0n && !isBorrowPreviewLoading && (
                    <ApyChange currentAPY={borrowAPY ?? 0} newAPY={borrowPreviewNew} type="cost" />
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-sm tracking-[-0.3px]">Supply APY</span>
                <div className="flex items-center gap-1.5 text-sm tracking-[-0.3px]">
                  <SupplyApyTooltip
                    breakdown={{
                      lendingApy: collateralLendingAPY ?? 0,
                      intrinsicApy: collateralIntrinsicAPY,
                      totalApy: supplyAPY ?? 0,
                    }}
                    showPointsSection={false}
                  >
                    <span className="text-muted-foreground">
                      {supplyAPY !== undefined ? `${supplyAPY.toFixed(2)}%` : '-'}
                    </span>
                  </SupplyApyTooltip>
                  {supplyPreviewNew !== undefined &&
                    collateralAmountBigInt > 0n &&
                    !isSupplyPreviewLoading &&
                    (() => {
                      const newTotal = addIntrinsicAPY(supplyPreviewNew, collateralIntrinsicAPY)
                      return <ApyChange currentAPY={supplyAPY ?? 0} newAPY={newTotal} type="earning" />
                    })()}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-sm tracking-[-0.3px]">Liquidation LTV</span>
                <span className="text-muted-foreground text-sm tracking-[-0.3px]">
                  {liquidationLTVPercent.toFixed(2)}%
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-sm tracking-[-0.3px]">Health Score</span>
                <span className="text-muted-foreground text-sm tracking-[-0.3px]">
                  {finalLtvPercent > 0
                    ? liquidationLTVPercent / finalLtvPercent > 100
                      ? '>100'
                      : (liquidationLTVPercent / finalLtvPercent).toFixed(2)
                    : '∞'}
                </span>
              </div>
            </div>
          </>
        )}
      </form>
    </FormProvider>
  )
}

function MultiplySection({
  collateralToken,
  liabilityToken,
  collateralBalance,
  maxLTVPercent,
  liquidationLTVPercent,
  hasLtvData,
  borrowAPY,
  supplyAPY,
  collateralIntrinsicAPY,
  collateralLendingAPY,
  userAddress,
  collateralVault,
  liabilityVault,
  collateralAsset,
  liabilityAsset,
  evcAddress,
  collateralPriceUSD,
  liabilityPriceUSD,
  collateralSymbol,
  totalBorrowed,
  borrowCap,
  availableLiquidity,
  collateralMaxDeposit,
  supportsNativeCollateral,
  supportsNativeBorrow: _supportsNativeBorrow, // Unused - multiply reinvests borrowed funds
  nativeHypeToken,
  nativeBalance,
  slippage,
  selectedSubAccountId,
  existingCollateralUsd = 0,
  existingDebtUsd = 0,
  // Controlled inputs
  controlledCollateralAmount,
  onControlledCollateralAmountChange,
  controlledBorrowAmount,
  onControlledBorrowAmountChange,
  controlledUseNativeCollateral,
}: SectionProps) {
  void _supportsNativeBorrow // Silence unused warning
  void onControlledCollateralAmountChange // Silence unused warning - multiply uses form state

  // Check if we're in controlled mode (inputs are in parent component)
  const isControlled = controlledCollateralAmount !== undefined || controlledBorrowAmount !== undefined
  const openDialog = useOpenDialog()
  const { connectWallet } = usePrivy()

  // Find available sub-account for this liability vault
  // For multiply, we ALWAYS need a fresh sub-account because the deposit must happen
  // before debt is incurred on that sub-account for liquidity checks to pass
  const {
    availableSubAccountId,
    allSubAccountsUsed,
    isLoading: isSubAccountLoading,
  } = useAvailableSubAccount({
    liabilityVault,
    chainId: BORROW_VAULT_CHAIN_ID,
  })

  // Use selectedSubAccountId if provided (user selected a specific position),
  // otherwise use a fresh sub-account for multiply operations
  const subAccountId = selectedSubAccountId !== undefined ? selectedSubAccountId : availableSubAccountId

  const form = useForm<BorrowFormData>({
    defaultValues: {
      collateralAmount: '',
      borrowAmount: '',
      ltv: 0,
      isMaxCollateral: false,
      isMaxBorrow: false,
      // Default to the asset with higher balance (native HYPE or WHYPE)
      collateralAssetIndex:
        supportsNativeCollateral && nativeBalance && Number(nativeBalance) > Number(collateralBalance) ? 0 : 1,
      receiveNativeHype: false, // For multiply, borrowed funds are used for more collateral, not withdrawn
    },
  })

  const collateralAmount = form.watch('collateralAmount')
  const ltv = form.watch('ltv')
  const collateralAssetIndex = form.watch('collateralAssetIndex')
  const hasCollateralInput = collateralAmount && Number(collateralAmount) > 0

  // Sync controlled collateral amount from parent to form (one-way: parent → form)
  // biome-ignore lint/correctness/useExhaustiveDependencies: collateralAmount intentionally excluded to prevent infinite loop
  useEffect(() => {
    if (controlledCollateralAmount !== undefined && controlledCollateralAmount !== collateralAmount) {
      form.setValue('collateralAmount', controlledCollateralAmount, { shouldValidate: false })
    }
  }, [controlledCollateralAmount, form])

  // Sync calculated borrow amount to parent if in controlled mode
  // The borrow amount is calculated below based on LTV and collateral, we just need to emit it
  // We'll do this in the calculation/slider effect logic

  // Calculate current position LTV for existing positions
  const currentPositionLtv = useMemo(() => {
    if (existingDebtUsd > 0 && existingCollateralUsd > 0) {
      return (existingDebtUsd / existingCollateralUsd) * 100
    }
    return 0
  }, [existingDebtUsd, existingCollateralUsd])

  // Calculate effective LTV AFTER new collateral is added (for slider min)
  // This is important: when user adds collateral, the effective LTV goes DOWN
  const effectiveCurrentLtv = useMemo(() => {
    const cNewUsd = collateralAmount && Number(collateralAmount) > 0 ? Number(collateralAmount) * collateralPriceUSD : 0
    const cTotalUsd = existingCollateralUsd + cNewUsd
    if (existingDebtUsd > 0 && cTotalUsd > 0) {
      return (existingDebtUsd / cTotalUsd) * 100
    }
    return 0
  }, [existingDebtUsd, existingCollateralUsd, collateralAmount, collateralPriceUSD])

  // Initialize slider to current position's LTV when modifying existing position
  const [hasInitializedLtv, setHasInitializedLtv] = useState(false)
  useEffect(() => {
    if (!hasInitializedLtv && currentPositionLtv > 0) {
      form.setValue('ltv', currentPositionLtv)
      setHasInitializedLtv(true)
    }
  }, [currentPositionLtv, hasInitializedLtv, form])

  // When collateral changes, update LTV to effective current LTV (accounts for new collateral)
  useEffect(() => {
    if (!collateralAmount || Number(collateralAmount) <= 0) {
      // No collateral input - reset to current position LTV
      form.setValue('ltv', currentPositionLtv)
    } else {
      // Has collateral input - set to effective current LTV (lower due to added collateral)
      // but only if the current form LTV is below the new minimum
      const currentFormLtv = form.getValues('ltv')
      if (currentFormLtv < effectiveCurrentLtv) {
        form.setValue('ltv', effectiveCurrentLtv)
      }
    }
  }, [collateralAmount, form, currentPositionLtv, effectiveCurrentLtv])

  // Update collateral asset selection when balances load (only if user hasn't manually changed it)
  const [hasUserChangedAsset, setHasUserChangedAsset] = useState(false)
  useEffect(() => {
    if (!hasUserChangedAsset && supportsNativeCollateral && nativeBalance && collateralBalance) {
      const shouldSelectNative = Number(nativeBalance) > Number(collateralBalance)
      const currentIndex = form.getValues('collateralAssetIndex')
      const expectedIndex = shouldSelectNative ? 0 : 1

      // Only update if the balance-based selection differs from current
      if (currentIndex !== expectedIndex) {
        form.setValue('collateralAssetIndex', expectedIndex)
      }
    }
  }, [hasUserChangedAsset, supportsNativeCollateral, nativeBalance, collateralBalance, form])

  // Determine if using native HYPE for collateral
  // In controlled mode (Quick Borrow), use the controlled value from parent
  // Otherwise, use the form's collateralAssetIndex (0 = native, 1 = wrapped)
  const isNativeCollateral = isControlled
    ? (controlledUseNativeCollateral ?? false) && supportsNativeCollateral
    : supportsNativeCollateral && collateralAssetIndex === 0

  // Build collateral asset options
  const collateralAssets = useMemo(() => {
    const assets: { token: Token; balance: NormalizedUnitNumber }[] = []

    // Native HYPE first if supported (gas reserve already applied to nativeBalance prop)
    if (supportsNativeCollateral && nativeHypeToken && nativeBalance) {
      assets.push({ token: nativeHypeToken, balance: nativeBalance })
    }

    // WHYPE/underlying token
    if (collateralToken) {
      assets.push({ token: collateralToken, balance: collateralBalance })
    }

    return assets
  }, [supportsNativeCollateral, nativeHypeToken, nativeBalance, collateralToken, collateralBalance])

  // Get selected collateral token and balance
  // In controlled mode, use isNativeCollateral to determine the correct token/balance
  // In non-controlled mode, use collateralAssetIndex from the form
  const selectedCollateralToken = isControlled
    ? isNativeCollateral && nativeHypeToken
      ? nativeHypeToken
      : collateralToken
    : collateralAssets[collateralAssetIndex]?.token ?? collateralToken
  const selectedCollateralBalance = isControlled
    ? isNativeCollateral && nativeBalance
      ? nativeBalance
      : collateralBalance
    : collateralAssets[collateralAssetIndex]?.balance ?? collateralBalance

  // Calculate multiplier from LTV
  // If leveraging existing position: Multiplier = (Existing Collateral + New Deposit) / ((Existing Collateral + New Deposit) - (Existing Debt + New Borrow))
  // But the UI defines Multiplier based on the slider/LTV target.
  const multiplier = ltv > 0 && ltv < 100 ? 1 / (1 - ltv / 100) : 1

  // Local state for multiplier input to prevent focus loss on every keystroke
  const [multiplierInputValue, setMultiplierInputValue] = useState('')
  const [isMultiplierInputFocused, setIsMultiplierInputFocused] = useState(false)

  // Sync local input value with multiplier value when not focused (e.g., when slider changes)
  useEffect(() => {
    if (!isMultiplierInputFocused) {
      setMultiplierInputValue(multiplier.toFixed(2))
    }
  }, [multiplier, isMultiplierInputFocused])

  // Calculate borrow amount based on multiplier and price ratio
  // D_new = (LTV_target * (C_existing + C_new) - D_existing) / (1 - LTV_target)
  // But wait, the multiplier is defined as M = 1 / (1 - LTV).
  // So LTV = 1 - 1/M.
  // 1 - LTV = 1/M.
  // D_new = (LTV_target * (C_total_collateral) - D_existing) / (1 - LTV_target)
  // This formula accounts for swap slippage if C_total_collateral includes swapped amount? No.
  // Let's stick to the derivation:
  // D_new = LTV * (C_ex + C_new + D_new) - D_ex  (Assuming D_new ~= C_swapped, ignoring slippage/fees for estimation)
  // D_new - LTV * D_new = LTV * (C_ex + C_new) - D_ex
  // D_new (1 - LTV) = LTV * (C_ex + C_new) - D_ex
  // D_new = [ LTV * (C_ex + C_new) - D_ex ] / (1 - LTV)

  // Let's verify existing formula matches this when D_ex=0, C_ex=0:
  // D_new = [ LTV * C_new ] / (1 - LTV)
  // Current code: ((C_new * (M - 1)) / Price)
  // M - 1 = (1 / (1-LTV)) - 1 = LTV / (1-LTV)
  // So C_new * LTV / (1-LTV). This matches.

  // So we just need to use the more general formula.
  // Note: All values in USD for the calculation, then convert to tokens.

  const borrowAmount = useMemo(() => {
    if (ltv <= 0 || liabilityPriceUSD <= 0) return '0'

    const cNewUsd = collateralAmount && Number(collateralAmount) > 0 ? Number(collateralAmount) * collateralPriceUSD : 0
    const cTotalUsd = existingCollateralUsd + cNewUsd
    const ltvDecimal = ltv / 100

    // D_new = [ LTV * (C_ex + C_new) - D_ex ] / (1 - LTV)
    // Numerator
    const numerator = ltvDecimal * cTotalUsd - existingDebtUsd
    // Denominator
    const denominator = 1 - ltvDecimal

    if (denominator <= 0) return '0' // Should not happen given ltv < 100 constraint

    const dNewUsd = Math.max(0, numerator / denominator)
    const dNewTokens = dNewUsd / liabilityPriceUSD

    return dNewTokens > 0 ? dNewTokens.toFixed(6) : '0'
  }, [collateralAmount, ltv, liabilityPriceUSD, existingCollateralUsd, existingDebtUsd, collateralPriceUSD])

  // If in controlled mode, we need to update the parent with the calculated borrow amount
  // This effectively makes the "Borrow Amount" input in MarketExplorer read-only/derived
  const prevBorrowAmountRef = useRef(borrowAmount)
  useEffect(() => {
    if (isControlled && onControlledBorrowAmountChange && borrowAmount !== prevBorrowAmountRef.current) {
      // Only update if it actually changed to avoid loops, though the calculation is stable
      prevBorrowAmountRef.current = borrowAmount
      // We pass the calculated amount up.
      // Note: MarketExplorer might have this input editable.
      // Ideally, MarketExplorer should disable the input when in Multiply mode.
      onControlledBorrowAmountChange(borrowAmount)
    }
  }, [borrowAmount, isControlled, onControlledBorrowAmountChange])

  // Validate borrow against cap and liquidity
  const borrowValidation = useMemo(() => {
    return validateEulerBorrow({
      value: borrowAmount && borrowAmount !== '0' ? NormalizedUnitNumber(borrowAmount) : undefined,
      totalBorrowed: totalBorrowed || NormalizedUnitNumber('0'),
      borrowCap,
      availableLiquidity: availableLiquidity || NormalizedUnitNumber('0'),
      symbol: liabilityToken?.symbol,
    })
  }, [borrowAmount, totalBorrowed, borrowCap, availableLiquidity, liabilityToken?.symbol])

  // Validate the total leveraged deposit against supply cap / maxDeposit.
  // In multiply mode the vault receives collateralAmount * multiplier (initial deposit
  // plus the borrowed-and-swapped amount re-deposited), so we must validate the full amount.
  const leveragedDepositAmount = useMemo(() => {
    if (!collateralAmount || Number(collateralAmount) <= 0) return undefined
    return (Number(collateralAmount) * multiplier).toString()
  }, [collateralAmount, multiplier])

  const supplyValidation = useMemo(() => {
    return validateMaxDeposit({
      depositAmount: leveragedDepositAmount,
      maxDeposit: collateralMaxDeposit,
      symbol: selectedCollateralToken?.symbol || collateralSymbol,
    })
  }, [leveragedDepositAmount, collateralMaxDeposit, selectedCollateralToken?.symbol, collateralSymbol])

  // Preview APY: use leveraged deposit (collateral × multiplier) since multiply
  // loops deposit→borrow→swap→deposit, so the vault receives the full leveraged amount
  const multiplyCollateralBigInt = useMemo(() => {
    if (!leveragedDepositAmount || !collateralToken) return 0n
    try {
      return parseUnits(leveragedDepositAmount, collateralToken.decimals)
    } catch {
      return 0n
    }
  }, [leveragedDepositAmount, collateralToken])

  const multiplyBorrowBigInt = useMemo(() => {
    if (!borrowAmount || borrowAmount === '0' || !liabilityToken) return 0n
    try {
      return parseUnits(borrowAmount, liabilityToken.decimals)
    } catch {
      return 0n
    }
  }, [borrowAmount, liabilityToken])

  const { newAPY: multiplySupplyPreviewNew, isLoading: isMultiplySupplyPreviewLoading } = usePreviewSupplyAPY({
    vaultAddress: collateralVault,
    changeAmount: multiplyCollateralBigInt,
    changeType: 'deposit',
    chainId: BORROW_VAULT_CHAIN_ID,
    enabled: multiplyCollateralBigInt > 0n,
  })

  const { newBorrowAPY: multiplyBorrowPreviewNew, isLoading: isMultiplyBorrowPreviewLoading } = usePreviewSupplyAPY({
    vaultAddress: liabilityVault,
    changeAmount: multiplyBorrowBigInt,
    changeType: 'borrow',
    chainId: BORROW_VAULT_CHAIN_ID,
    enabled: multiplyBorrowBigInt > 0n,
  })

  // Calculate liquidity-constrained max LTV
  // Solving D_new = [ LTV * cTotalUsd - existingDebtUsd ] / (1 - LTV) for LTV when D_new = availableLiquidity:
  // LTV = (availableLiquidityUSD + existingDebtUsd) / (cTotalUsd + availableLiquidityUSD)
  const liquidityConstrainedMaxLTV = useMemo(() => {
    if (!availableLiquidity || liabilityPriceUSD <= 0) return maxLTVPercent

    const cNewUsd = collateralAmount && Number(collateralAmount) > 0 ? Number(collateralAmount) * collateralPriceUSD : 0
    const cTotalUsd = existingCollateralUsd + cNewUsd
    if (cTotalUsd <= 0) return maxLTVPercent

    const availableLiquidityUsd = Number(availableLiquidity) * liabilityPriceUSD
    const constrainedLtv = ((availableLiquidityUsd + existingDebtUsd) / (cTotalUsd + availableLiquidityUsd)) * 100

    return Math.min(maxLTVPercent, constrainedLtv)
  }, [
    availableLiquidity,
    liabilityPriceUSD,
    collateralAmount,
    collateralPriceUSD,
    existingCollateralUsd,
    existingDebtUsd,
    maxLTVPercent,
  ])

  // Use the more restrictive of LTV-based max and liquidity-based max
  const effectiveMaxLTV = liquidityConstrainedMaxLTV
  const isLiquidityConstrained = effectiveMaxLTV < maxLTVPercent - 0.1 // 0.1% tolerance

  const handleMultiply = form.handleSubmit(() => {
    if (!selectedCollateralToken || !collateralAsset || !liabilityToken || !liabilityAsset) {
      console.error('[MultiplySection] Missing required data:', {
        selectedCollateralToken: !!selectedCollateralToken,
        collateralAsset,
        liabilityToken: !!liabilityToken,
        liabilityAsset,
      })
      return
    }

    // Use new EVC multiply dialog which uses Euler's full wagmi-evc stack
    // for proper simulation, debug tracing, and transaction execution
    openDialog(evcMultiplyDialogConfig, {
      collateralVault,
      collateralSymbol: String(selectedCollateralToken.symbol),
      collateralAmount,
      collateralDecimals: selectedCollateralToken.decimals,
      collateralAsset,
      ...(selectedCollateralToken.logoURI && { collateralLogoURI: selectedCollateralToken.logoURI }),
      liabilityVault,
      liabilitySymbol: String(liabilityToken.symbol),
      borrowAmount,
      liabilityDecimals: liabilityToken.decimals,
      liabilityAsset,
      ...(liabilityToken.logoURI && { liabilityLogoURI: liabilityToken.logoURI }),
      ...(subAccountId !== null && { subAccountId }),
      // Native HYPE flags - for multiply, we wrap collateral but don't unwrap borrow
      ...(isNativeCollateral && { wrapCollateralFromNative: true }),
      // Multiplier for UI display
      multiplier,
      // Slippage for swap operations
      slippage: slippage ? Number(slippage) * 100 : 0.5,
      isModifyingExistingPosition,
    })
  })

  const hasExistingPositionData = existingCollateralUsd > 0
  const isModifyingExistingPosition = hasExistingPositionData && selectedSubAccountId !== undefined

  // Can multiply if:
  // 1. New position: user is depositing collateral AND has selected leverage above 1x
  // 2. Existing position: user is levering up beyond the current effective LTV
  // Note: use effectiveCurrentLtv which accounts for new collateral being added.
  const hasValidInput =
    (hasCollateralInput && Number(borrowAmount) > 0) || (isModifyingExistingPosition && ltv > effectiveCurrentLtv)
  const balanceOk = !hasCollateralInput || Number(collateralAmount) <= Number(selectedCollateralBalance)

  const canMultiply =
    hasLtvData && // LTV data must be available
    hasValidInput && // Must have new deposit OR be levering up existing position
    balanceOk && // Check balance only if depositing
    evcAddress &&
    borrowValidation.canProceed &&
    supplyValidation.canProceed &&
    (isModifyingExistingPosition || !allSubAccountsUsed) && // Skip sub-account check when modifying existing position
    !isSubAccountLoading // Wait for subaccount discovery to complete

  // Calculate new collateral value in USD
  const newCollateralUsd =
    collateralAmount && Number(collateralAmount) > 0 ? Number(collateralAmount) * collateralPriceUSD : 0
  // Multiplier only applies to new deposit - existing collateral stays as-is
  // Total position = existing collateral + (new deposit * multiplier) + auto-purchased from borrowing
  const leveragedNewCollateralUsd = newCollateralUsd * multiplier
  // Auto-purchased collateral from borrowing (when levering up existing position without new deposit)
  const autoPurchasedUsd =
    Number(borrowAmount) > 0 && liabilityPriceUSD > 0 ? Number(borrowAmount) * liabilityPriceUSD : 0
  const totalPositionValueUsd =
    existingCollateralUsd + leveragedNewCollateralUsd + (hasCollateralInput ? 0 : autoPurchasedUsd)
  const totalPositionTokens = collateralPriceUSD > 0 ? totalPositionValueUsd / collateralPriceUSD : 0

  return (
    <FormProvider {...form}>
      <form onSubmit={handleMultiply} className="flex flex-col gap-4">
        {/* Overleveraged Position Warning - show when position is still overleveraged even after adding collateral */}
        {effectiveCurrentLtv >= effectiveMaxLTV && hasCollateralInput && (
          <div className="rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-2">
            <p className="text-red-400 text-xs">
              ⚠️ Position is overleveraged. Add more collateral to bring LTV below {effectiveMaxLTV.toFixed(1)}%. Current
              effective LTV: {effectiveCurrentLtv.toFixed(1)}%
            </p>
          </div>
        )}

        {/* Collateral Input - hide in controlled mode (quick borrow has its own) */}
        {!isControlled && (
          <div className="flex flex-col gap-2">
            <label className="text-muted-foreground text-xs">Deposit</label>
            <div className="flex gap-2">
              {selectedCollateralToken && (
                <>
                  <ControlledMultiSelectorAssetInput
                    fieldName="collateralAmount"
                    control={form.control}
                    token={selectedCollateralToken}
                    max={selectedCollateralBalance}
                    maxSelectedFieldName="isMaxCollateral"
                    balance={selectedCollateralBalance}
                    balanceType="wallet"
                    disabled={!userAddress}
                  />
                  <AssetSelector
                    assets={collateralAssets}
                    selectedAsset={selectedCollateralToken}
                    setSelectedAsset={(symbol) => {
                      const idx = collateralAssets.findIndex((a) => a.token.symbol === symbol)
                      if (idx >= 0) {
                        form.setValue('collateralAssetIndex', idx)
                        form.setValue('collateralAmount', '') // Reset amount when switching
                        form.setValue('borrowAmount', '') // Reset borrow amount for shared form state
                        form.setValue('isMaxCollateral', false) // Reset max button state
                        form.setValue('isMaxBorrow', false) // Reset max button state for shared form state
                        setHasUserChangedAsset(true) // Mark that user manually changed selection
                      }
                    }}
                    disabled={collateralAssets.length <= 1}
                  />
                </>
              )}
            </div>
          </div>
        )}

        {/* Multiplier Slider */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <label className="text-muted-foreground text-xs">Multiplier</label>
            <div className="flex items-center gap-1">
              <input
                type="text"
                inputMode="decimal"
                value={multiplierInputValue}
                onChange={(e) => {
                  const normalized = normalizeDecimalInput(e.target.value)
                  if (normalized === null) return

                  setMultiplierInputValue(normalized)
                  if (normalized && !normalized.endsWith('.') && !Number.isNaN(Number(normalized))) {
                    const newMultiplier = Math.min(Math.max(1, Number(normalized)), 1 / (1 - effectiveMaxLTV / 100))
                    // Convert multiplier back to LTV: ltv = (1 - 1/multiplier) * 100
                    const newLtv = newMultiplier > 1 ? (1 - 1 / newMultiplier) * 100 : 0
                    form.setValue('ltv', newLtv)
                  }
                }}
                onFocus={() => setIsMultiplierInputFocused(true)}
                onBlur={() => {
                  setIsMultiplierInputFocused(false)
                  setMultiplierInputValue(multiplier.toFixed(2))
                }}
                className="w-16 rounded border border-white/10 bg-white/[0.04] px-2 py-0.5 text-right text-white text-xs outline-none focus:border-primary"
                disabled={!userAddress || (!hasCollateralInput && existingCollateralUsd <= 0)}
              />
              <span className="text-muted-foreground text-xs">x</span>
            </div>
          </div>
          <Slider
            value={[Math.min(Math.max(ltv, effectiveCurrentLtv), effectiveMaxLTV)]}
            min={Math.min(effectiveCurrentLtv, effectiveMaxLTV)}
            max={effectiveMaxLTV}
            step={0.1}
            onValueChange={(values) => {
              const minLtv = Math.min(effectiveCurrentLtv, effectiveMaxLTV)
              form.setValue('ltv', values[0] || minLtv)
            }}
            disabled={
              !userAddress ||
              (!hasCollateralInput && existingCollateralUsd <= 0) ||
              effectiveCurrentLtv >= effectiveMaxLTV
            }
            trackClassName="bg-gradient-to-r from-emerald-500 via-amber-500 via-70% to-red-500"
            fixedGradient
          />
          <div className="flex items-center justify-between text-white/30 text-xs">
            <span>
              {effectiveCurrentLtv > 0
                ? `Current: ${effectiveCurrentLtv < 100 ? (1 / (1 - effectiveCurrentLtv / 100)).toFixed(2) : '∞'}x`
                : '1x'}
            </span>
            <span className={isLiquidityConstrained ? 'text-amber-400' : ''}>
              Max: {effectiveMaxLTV < 100 ? (1 / (1 - effectiveMaxLTV / 100)).toFixed(2) : '∞'}x
              {isLiquidityConstrained && ' (liquidity)'}
            </span>
          </div>
        </div>

        {/* Liquidity Warning - show even in controlled mode */}
        {isLiquidityConstrained && (
          <div className="rounded-lg border border-amber-500/20 bg-amber-500/10 px-3 py-2">
            <p className="text-amber-400 text-xs">
              ⚠️ Max leverage limited by available liquidity ({Number(availableLiquidity).toFixed(2)}{' '}
              {liabilityToken?.symbol} available)
            </p>
          </div>
        )}

        {/* Position Breakdown */}
        <div className="flex flex-col gap-3 rounded-xl border bg-card p-4">
          <div className="mb-1 border-white/10 border-b pb-2">
            <span className="font-medium text-muted-foreground text-xs uppercase tracking-wide">Position Summary</span>
          </div>

          {/* Show existing position info when modifying */}
          {existingCollateralUsd > 0 && (
            <>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground text-sm">Existing Position</span>
                <span className="text-sm text-white/70">
                  {collateralPriceUSD > 0
                    ? `${(existingCollateralUsd / collateralPriceUSD).toFixed(4)} ${selectedCollateralToken?.symbol || ''}`
                    : '-'}
                </span>
              </div>
              {existingDebtUsd > 0 && liabilityPriceUSD > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground text-sm">Existing Debt</span>
                  <span className="text-red-400/70 text-sm">
                    {(existingDebtUsd / liabilityPriceUSD).toFixed(4)} {liabilityToken?.symbol || ''}
                  </span>
                </div>
              )}
              <div className="border-white/10 border-t" />
            </>
          )}

          {/* Your Deposit */}
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-sm">
              {existingCollateralUsd > 0 ? 'Additional Deposit' : 'Your Deposit'}
            </span>
            <span className="text-sm text-white">
              {collateralAmount && Number(collateralAmount) > 0
                ? `${Number(collateralAmount).toFixed(4)} ${selectedCollateralToken?.symbol || ''}`
                : existingCollateralUsd > 0 || ltv > 0
                  ? `0 ${selectedCollateralToken?.symbol || ''}`
                  : '-'}
            </span>
          </div>

          {/* Debt Created */}
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-sm">{existingDebtUsd > 0 ? 'New Debt' : 'Debt Created'}</span>
            <span className={`text-sm ${Number(borrowAmount) > 0 ? 'text-red-400' : 'text-muted-foreground'}`}>
              {hasCollateralInput || existingCollateralUsd > 0 || ltv > 0
                ? Number(borrowAmount) > 0
                  ? `${Number(borrowAmount).toFixed(4)} ${liabilityToken?.symbol || ''}`
                  : `0 ${liabilityToken?.symbol || ''}`
                : '-'}
            </span>
          </div>

          {/* Additional Collateral Bought */}
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-sm">+ Auto-purchased</span>
            <span className={`text-sm ${Number(borrowAmount) > 0 ? 'text-emerald-400' : 'text-muted-foreground'}`}>
              {(hasCollateralInput || existingCollateralUsd > 0 || ltv > 0) &&
              liabilityPriceUSD > 0 &&
              collateralPriceUSD > 0
                ? Number(borrowAmount) > 0
                  ? `+${((Number(borrowAmount) * liabilityPriceUSD) / collateralPriceUSD).toFixed(4)} ${selectedCollateralToken?.symbol || ''}`
                  : `0 ${selectedCollateralToken?.symbol || ''}`
                : '-'}
            </span>
          </div>

          {/* Divider */}
          <div className="border-white/10 border-t" />

          {/* ROE (Return on Equity) — shows currentROE → targetROE based on multiplier
              For new positions: 0.00% → X.XX%
              For existing positions: currentROE% → targetROE% (derived from existing LTV) */}
          {supplyAPY !== undefined &&
            borrowAPY !== undefined &&
            (() => {
              // ROE = supplyAPY + (multiplier - 1) * (supplyAPY - borrowAPY)
              const netApy = supplyAPY - borrowAPY
              // Use effectiveCurrentLtv (accounts for additional deposit) so the "from" ROE
              // matches the "Current: X.XXx" multiplier shown on the slider.
              const effectiveLtv = isModifyingExistingPosition ? effectiveCurrentLtv : 0
              const currentMultiplier = effectiveLtv > 0 && effectiveLtv < 100 ? 1 / (1 - effectiveLtv / 100) : 1
              const existingRoe = isModifyingExistingPosition ? supplyAPY + (currentMultiplier - 1) * netApy : 0
              // Show 0 when no position is configured (no deposit entered and not adjusting existing)
              const hasPosition = hasCollateralInput || isModifyingExistingPosition
              const targetRoe = hasPosition ? supplyAPY + (multiplier - 1) * netApy : 0
              const showArrow = existingRoe !== targetRoe
              return (
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm text-white">ROE</span>
                  <span className="font-semibold text-sm">
                    {showArrow && (
                      <>
                        <span className={existingRoe >= 0 ? 'text-muted-foreground' : 'text-red-400'}>
                          {existingRoe.toFixed(2)}%
                        </span>
                        <span className="mx-1 text-muted-foreground">→</span>
                      </>
                    )}
                    <span
                      className={
                        targetRoe >= 0 ? (showArrow ? 'text-emerald-400' : 'text-muted-foreground') : 'text-red-400'
                      }
                    >
                      {targetRoe.toFixed(2)}%
                    </span>
                  </span>
                </div>
              )
            })()}

          {/* Total Position */}
          <div className="flex items-center justify-between">
            <span className="font-medium text-sm text-white">Total Position</span>
            <span className="font-semibold text-base text-white">
              {totalPositionTokens > 0
                ? `${totalPositionTokens.toFixed(4)} ${selectedCollateralToken?.symbol || ''}`
                : existingCollateralUsd > 0 && collateralPriceUSD > 0
                  ? `${(existingCollateralUsd / collateralPriceUSD).toFixed(4)} ${selectedCollateralToken?.symbol || ''}`
                  : '-'}
            </span>
          </div>

          {/* Total Debt (when there's existing debt, show total) */}
          {(existingDebtUsd > 0 || Number(borrowAmount) > 0) && liabilityPriceUSD > 0 && (
            <div className="flex items-center justify-between">
              <span className="font-medium text-sm text-white">Total Debt</span>
              <span className="font-semibold text-base text-red-400">
                {((existingDebtUsd + Number(borrowAmount) * liabilityPriceUSD) / liabilityPriceUSD).toFixed(4)}{' '}
                {liabilityToken?.symbol || ''}
              </span>
            </div>
          )}

          {/* Hint when no deposit entered (new position) */}
          {ltv > 0 && Number(borrowAmount) === 0 && !hasCollateralInput && existingCollateralUsd <= 0 && (
            <div className="mt-1 rounded-lg bg-amber-500/10 px-3 py-2">
              <p className="text-amber-400 text-xs">
                <span className="mr-1">💡</span>
                Enter a deposit amount above to see your multiplied position.
              </p>
            </div>
          )}

          {/* Wallet receives nothing notice */}
          {multiplier > 1 && (hasCollateralInput || (isModifyingExistingPosition && ltv > currentPositionLtv)) && (
            <div className="mt-1 rounded-lg bg-blue-500/10 px-3 py-2">
              <p className="text-blue-400 text-xs">
                <span className="mr-1">ⓘ</span>
                Multiply creates leveraged exposure. No tokens are sent to your wallet — borrowed funds are
                automatically used to purchase more {selectedCollateralToken?.symbol || 'collateral'}.
              </p>
            </div>
          )}
        </div>

        {!userAddress ? (
          <Button type="button" onClick={connectWallet} variant="primary" className="w-full">
            Connect Wallet
          </Button>
        ) : (
          <>
            <Button type="submit" disabled={!canMultiply} variant="primary" className="w-full">
              {allSubAccountsUsed
                ? 'Max Positions Reached'
                : !hasLtvData
                  ? 'LTV Data Unavailable'
                  : isModifyingExistingPosition
                    ? 'Adjust Position'
                    : 'Create Multiply Position'}
            </Button>
            <p className="text-center text-muted-foreground text-xs">
              {isModifyingExistingPosition
                ? 'Adjusts your existing leveraged position'
                : 'Creates a new leveraged position'}
            </p>
            {!hasLtvData && (
              <div className="mt-2 rounded-lg border border-yellow-500/20 bg-yellow-500/10 p-3">
                <p className="text-sm text-yellow-400">
                  Max LTV and Liquidation LTV data are not available for this vault pair. Opening a position is
                  currently disabled for safety.
                </p>
              </div>
            )}
          </>
        )}

        {/* Validation errors - displayed under button */}
        <ValidationAlert validation={supplyValidation} />
        {(!isControlled || borrowValidation.severity === 'error') && <ValidationAlert validation={borrowValidation} />}

        {/* Info block below button */}
        <div className="flex flex-col gap-2 rounded-xl border bg-card p-4">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-sm tracking-[-0.3px]">Borrow APY</span>
            <div className="flex items-center gap-1.5 text-sm tracking-[-0.3px]">
              <span className="text-muted-foreground">
                {borrowAPY !== undefined ? `${borrowAPY.toFixed(2)}%` : '-'}
              </span>
              {multiplyBorrowPreviewNew !== undefined &&
                multiplyBorrowBigInt > 0n &&
                !isMultiplyBorrowPreviewLoading && (
                  <ApyChange currentAPY={borrowAPY ?? 0} newAPY={multiplyBorrowPreviewNew} type="cost" />
                )}
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-sm tracking-[-0.3px]">Supply APY</span>
            <div className="flex items-center gap-1.5 text-sm tracking-[-0.3px]">
              <SupplyApyTooltip
                breakdown={{
                  lendingApy: collateralLendingAPY ?? 0,
                  intrinsicApy: collateralIntrinsicAPY,
                  totalApy: supplyAPY ?? 0,
                }}
                showPointsSection={false}
              >
                <span className="text-muted-foreground">
                  {supplyAPY !== undefined ? `${supplyAPY.toFixed(2)}%` : '-'}
                </span>
              </SupplyApyTooltip>
              {multiplySupplyPreviewNew !== undefined &&
                multiplyCollateralBigInt > 0n &&
                !isMultiplySupplyPreviewLoading &&
                (() => {
                  const newTotal = addIntrinsicAPY(multiplySupplyPreviewNew, collateralIntrinsicAPY)
                  return <ApyChange currentAPY={supplyAPY ?? 0} newAPY={newTotal} type="earning" />
                })()}
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-sm tracking-[-0.3px]">Liquidation LTV</span>
            <span className="text-muted-foreground text-sm tracking-[-0.3px]">{liquidationLTVPercent.toFixed(2)}%</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground text-sm tracking-[-0.3px]">Health Score</span>
            <span className="text-muted-foreground text-sm tracking-[-0.3px]">
              {ltv > 0 ? (liquidationLTVPercent / ltv > 100 ? '>100' : (liquidationLTVPercent / ltv).toFixed(2)) : '∞'}
            </span>
          </div>
        </div>
      </form>
    </FormProvider>
  )
}
